import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getMyRoles, getMySolutionAccess } from "@/api/adminApi";
import { hasSolutionAccess, SolutionKey } from "@/security/solutionAccess";

type RoleAccessContextValue = {
  loading: boolean;
  roles: string[];
  hasRole: (role: string) => boolean;
  canAccessSolution: (solution: SolutionKey) => boolean;
};

const RoleAccessContext = createContext<RoleAccessContextValue | null>(null);

export const RoleAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [catalogAccessKeys, setCatalogAccessKeys] = useState<Set<string> | null>(null);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const myRoles = await getMyRoles();
        setRoles(myRoles.map((r) => r.role).filter((r) => !!r));

        try {
          const mySolutionAccess = await getMySolutionAccess();
          const activeAccess = mySolutionAccess
            .filter((a) => a.canView)
            .map((a) => a.solutionKey.trim().toLowerCase());

          setCatalogAccessKeys(new Set(activeAccess));
        } catch {
          setCatalogAccessKeys(null);
        }
      } catch {
        setRoles([]);
        setCatalogAccessKeys(null);
      } finally {
        setLoading(false);
      }
    };

    loadRoles();
  }, []);

  const value = useMemo<RoleAccessContextValue>(() => {
    const normalized = new Set(roles.map((r) => r.trim().toLowerCase()));

    const hasRole = (role: string) => normalized.has(role.trim().toLowerCase());
    const canAccessSolution = (solution: SolutionKey) => {
      if (catalogAccessKeys !== null) {
        return catalogAccessKeys.has(solution.toLowerCase());
      }

      return hasSolutionAccess(solution, roles);
    };

    return {
      loading,
      roles,
      hasRole,
      canAccessSolution,
    };
  }, [loading, roles, catalogAccessKeys]);

  return <RoleAccessContext.Provider value={value}>{children}</RoleAccessContext.Provider>;
};

export function useRoleAccess(): RoleAccessContextValue {
  const ctx = useContext(RoleAccessContext);
  if (!ctx) {
    throw new Error("useRoleAccess must be used within RoleAccessProvider");
  }

  return ctx;
}
